/**
 * useScheduleDraftEditorUiState — ScheduleDraft 工作台本地交互状态入口。
 *
 * 本 hook 管理选中项、弹窗、侧栏、观察模式、Timetable Tab 与轨配置状态，
 * 并在草稿身份变化时重置工作台局部状态。服务端状态仍由 detail query 与
 * mutation hooks 管理。
 *
 * 设计文档 → docs/20_workspace_ux/21_workspace_layout.md
 * 骨架关联 → hooks/useScheduleDraftTrackConfig.js (轨配置), hooks/useScheduleDraftEditorSideEffects.js (跨层副作用), context/ScheduleDraftEditorWorkspaceContext.jsx (组合层)
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
    getDefaultEditingTrackId,
    resolveEditingTrackIdFromSourceTrackKey,
    resolveScheduleDraftEditingTrackId,
} from "../lib/workspace/buildScheduleDraftWorkspaceModels";
import useLocalStorage from "./useLocalStorage";
import useScheduleDraftTrackConfig from "./useScheduleDraftTrackConfig";

const SIDEBAR_PANEL_MODES = new Set(["selection", "operations", "reference", "demands", "agents", "history"]);
const OBSERVATION_MODES = new Set(["timeline", "pattern", "compare"]);
const PATTERN_TYPES = new Set(["weekly", "rotation", "demand"]);

const isSidebarPanelMode = (value) => SIDEBAR_PANEL_MODES.has(value);
const isObservationMode = (value) => OBSERVATION_MODES.has(value);
const isPatternType = (value) => PATTERN_TYPES.has(value);
const isBooleanValue = (value) => typeof value === "boolean";
const isStorageString = (value) => typeof value === "string" && value.length > 0;

function normalizeCellSelection(value) {
    if (value instanceof Set) {
        return new Set(value);
    }
    if (Array.isArray(value)) {
        return new Set(value);
    }
    return new Set();
}

export function useScheduleDraftEditorUiState(draft) {
    const initializedDraftIdRef = useRef(null);
    const defaultEditingTrackId = getDefaultEditingTrackId(draft);
    const trackConfig = useScheduleDraftTrackConfig(draft);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [addFlightOpen, setAddFlightOpen] = useState(false);
    const [addFlightContext, setAddFlightContext] = useState({ mode: "end" });
    const [sourceTrackOpen, setSourceTrackOpen] = useState(false);
    const [sourceTrackDialogContext, setSourceTrackDialogContext] = useState(null);
    const [tableImportOpen, setTableImportOpen] = useState(false);
    const [pendingFlightSelectionId, setPendingFlightSelectionId] = useState(null);
    const [currentEditingTrackId, setCurrentEditingTrackIdState] = useState(defaultEditingTrackId);
    const [timelineFocusWindow, setTimelineFocusWindow] = useState(null);
    const [selectedCells, setSelectedCellsState] = useState(() => new Set());
    const [hoveredCell, setHoveredCell] = useState(null);
    const [timetableQuickFilterQuery, setTimetableQuickFilterQuery] = useState("");
    const [sidebarPanelMode, setSidebarPanelMode] = useLocalStorage("sidebarPanelMode", "demands", { scope: "prefs", validate: isSidebarPanelMode });
    const [observationMode, setObservationMode] = useLocalStorage("observationMode", "timeline", { scope: "prefs", validate: isObservationMode });
    const [patternType, setPatternType] = useLocalStorage("patternType", "weekly", { scope: "prefs", validate: isPatternType });
    const [timelineCanvasCollapsed, setTimelineCanvasCollapsed] = useLocalStorage("workspace.timelineCanvasCollapsed", false, { scope: "prefs", validate: isBooleanValue });
    const [timetableTab, setTimetableTab] = useLocalStorage("timetableTab", "draft-current", { scope: "draft", draftId: draft?.id, validate: isStorageString });

    const setCurrentEditingTrackId = useCallback(
        (trackId) => {
            setCurrentEditingTrackIdState((currentTrackId) => resolveScheduleDraftEditingTrackId(draft, trackId, currentTrackId || defaultEditingTrackId));
        },
        [defaultEditingTrackId, draft],
    );

    const setTrackSourceKey = useCallback(
        (nextSourceTrack) => {
            const nextSourceTrackKey = typeof nextSourceTrack === "function" ? nextSourceTrack(timetableTab) : nextSourceTrack;
            setTimetableTab(nextSourceTrackKey);
            setCurrentEditingTrackIdState((currentTrackId) => resolveEditingTrackIdFromSourceTrackKey(draft, nextSourceTrackKey, currentTrackId || defaultEditingTrackId));
            setTimelineFocusWindow(null);
            setSelectedCellsState(new Set());
            setHoveredCell(null);
        },
        [defaultEditingTrackId, draft, setTimetableTab, timetableTab],
    );

    const setPatternTypeAndClearFocus = useCallback(
        (nextPatternType) => {
            setPatternType(nextPatternType);
            setTimelineFocusWindow(null);
        },
        [setPatternType],
    );

    const focusTimelineWindow = useCallback((focus) => {
        const timeWindow = focus?.timeWindow || focus;
        if (!timeWindow?.start || !timeWindow?.end) {
            return;
        }

        setTimelineFocusWindow({
            ...focus,
            timeWindow,
            label: focus?.label || focus?.title || "焦点窗口",
        });
    }, []);

    const clearTimelineFocusWindow = useCallback(() => {
        setTimelineFocusWindow(null);
    }, []);

    const setSelectedCells = useCallback((nextSelection) => {
        setSelectedCellsState((currentSelection) => normalizeCellSelection(typeof nextSelection === "function" ? nextSelection(currentSelection) : nextSelection));
    }, []);

    const clearSelectedCells = useCallback(() => {
        setSelectedCellsState(new Set());
        setHoveredCell(null);
    }, []);

    const openSourceTrackDialog = useCallback((context = null) => {
        const nextContext = context && (context.referenceOnly || context.initialValues) ? context : null;
        setSourceTrackDialogContext(nextContext);
        setSourceTrackOpen(true);
    }, []);

    const closeSourceTrackDialog = useCallback(() => {
        setSourceTrackOpen(false);
        setSourceTrackDialogContext(null);
    }, []);

    useEffect(() => {
        const draftIdentity = draft?.id || null;
        if (initializedDraftIdRef.current === draftIdentity) {
            return;
        }

        initializedDraftIdRef.current = draftIdentity;
        setSelectedItemId(null);
        setAddFlightOpen(false);
        setAddFlightContext({ mode: "end" });
        setSourceTrackOpen(false);
        setSourceTrackDialogContext(null);
        setTableImportOpen(false);
        setPendingFlightSelectionId(null);
        setCurrentEditingTrackIdState(defaultEditingTrackId);
        setTimelineFocusWindow(null);
        setSelectedCellsState(new Set());
        setHoveredCell(null);
        setTimetableQuickFilterQuery("");
    }, [defaultEditingTrackId, draft?.id]);

    useEffect(() => {
        if (!draft?.id) {
            return;
        }

        setCurrentEditingTrackIdState((currentTrackId) => resolveScheduleDraftEditingTrackId(draft, currentTrackId, defaultEditingTrackId));
    }, [defaultEditingTrackId, draft]);

    useEffect(() => {
        if (!draft?.id) {
            return;
        }

        setCurrentEditingTrackIdState((currentTrackId) => resolveEditingTrackIdFromSourceTrackKey(draft, timetableTab, currentTrackId || defaultEditingTrackId));
    }, [defaultEditingTrackId, draft, timetableTab]);

    const handleSelectItem = useCallback((item) => {
        setSelectedItemId(item?.id || null);
        if (item) {
            setSidebarPanelMode("selection");
        }
    }, [setSidebarPanelMode]);

    return {
        selectedItemId,
        setSelectedItemId,
        handleSelectItem,
        addFlightOpen,
        addFlightContext,
        openAddFlightDialog: (context = {}) => {
            setAddFlightContext({ mode: "end", ...(context || {}) });
            setAddFlightOpen(true);
        },
        closeAddFlightDialog: () => setAddFlightOpen(false),
        sourceTrackOpen,
        sourceTrackDialogContext,
        openSourceTrackDialog,
        closeSourceTrackDialog,
        tableImportOpen,
        openTableImportDialog: () => setTableImportOpen(true),
        closeTableImportDialog: () => setTableImportOpen(false),
        pendingFlightSelectionId,
        setPendingFlightSelectionId,
        clearPendingFlightSelection: () => setPendingFlightSelectionId(null),
        sidebarPanelMode,
        setSidebarPanelMode,
        observationMode,
        setObservationMode,
        sourceTrack: timetableTab,
        setSourceTrack: setTrackSourceKey,
        timetableTab,
        setTimetableTab: setTrackSourceKey,
        timetableQuickFilterQuery,
        setTimetableQuickFilterQuery,
        currentEditingTrackId,
        setCurrentEditingTrackId,
        timelineFocusWindow,
        focusTimelineWindow,
        clearTimelineFocusWindow,
        selectedCells,
        setSelectedCells,
        clearSelectedCells,
        hoveredCell,
        setHoveredCell,
        patternType,
        setPatternType: setPatternTypeAndClearFocus,
        timelineCanvasCollapsed,
        setTimelineCanvasCollapsed,
        ...trackConfig,
    };
}

export default useScheduleDraftEditorUiState;
